import { Button, Skeleton, Stack, ThemeIcon } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { ImageGalleryRevamped } from "@/components/elements/ImageGallery/ImageGalleryRevamped";
import { IconEdit } from "@/components/icons";
import { useUpdateCarGallery } from "@/services/cars/update-car-gallery";
import { useMarketCarImages } from "@/services/market/get-market-car-images";
import { useProfile } from "@/services/profile/get-profile";
import { showCustomNotification } from "@/utils/notifications";

import type { Attachment } from "@/types/attachment";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type CarImageGalleryRevampedProps = {
  carId?: string;
  taxonomyId?: string;
  images?: Attachment[];
  onEditorButtonClick?: () => void;
  loading?: boolean;
  title?: string;
  withEditorButton?: boolean;
  isPlaceholderVisible?: boolean;
} & MantineStyleProps;

export const CarImageGalleryRevamped: FC<CarImageGalleryRevampedProps> = ({
  carId,
  taxonomyId,
  images = [],
  onEditorButtonClick,
  loading = false,
  title,
  withEditorButton = false,
  isPlaceholderVisible = false,
  ...props
}) => {
  const { t } = useTranslation();
  const { data, isLoading } = useMarketCarImages({ carId: taxonomyId ?? carId });
  const { data: profile } = useProfile();

  const gallery = [...images, ...(data?.results || [])];
  const hiddenStockImageCarIds = profile?.additional_info.stockImagesHiddenCarIds ?? [];
  const hideStockImages = Boolean(carId && hiddenStockImageCarIds.includes(carId));
  const visibleGallery = hideStockImages ? gallery.filter((image) => Boolean(image.id)) : gallery;
  const { mutateAsync: updateGallery, isPending: isUpdating } = useUpdateCarGallery();

  async function handleInlineUpload(files: any[]) {
    if (!carId) return null;

    try {
      const filesToAdd = files.filter((file) => "file" in file).map((file) => file.file);

      const existingFileIds = new Set(files.filter((file) => "id" in file).map((file) => file.id));

      const fileIdsToRemove = (gallery || [])
        .filter((file) => file.id && !existingFileIds.has(file.id))
        .map((file) => file.id as string);

      const result = await updateGallery({
        carId: carId as string,
        filesToAdd,
        fileIdsToRemove,
      });

      // If API returns the new/updated items, try to extract and return them so placeholder can be replaced
      if (result) {
        if (Array.isArray(result)) return result;
        if ((result as any).results) return (result as any).results;
      }

      return result;
    } catch (error) {
      showCustomNotification({
        title: t("car.generic_error"),
        message: t("car.errorUploadingImage"),
        autoClose: 6000,
      });
      return null;
    }
  }

  async function handleRemoveImage(image: Attachment) {
    if (!carId) return false;

    try {
      await updateGallery({
        carId,
        filesToAdd: [],
        fileIdsToRemove: [image.id],
      });
      return true;
    } catch {
      showCustomNotification({
        title: t("car.generic_error"),
        message: t("car.errorDeletingImage"),
        autoClose: 6000,
      });
      return false;
    }
  }

  return (
    <Stack gap="md" align="flex-end" {...props}>
      <Skeleton visible={loading || isLoading}>
        <ImageGalleryRevamped
          images={visibleGallery}
          hideStockImages={hideStockImages}
          previewPosition="side"
          modalTitle={title}
          onUpload={handleInlineUpload}
          onPlaceholderClick={onEditorButtonClick}
          onEditorButtonClick={onEditorButtonClick}
          onRemoveImage={withEditorButton ? handleRemoveImage : undefined}
          removableImageIds={images.map((image) => image.id)}
          uploading={isUpdating}
          isPlaceholderVisible={isPlaceholderVisible}
          uploadingButton={
            withEditorButton && gallery.length > 0 ? (
              <Button
                p={0}
                leftSection={
                  <ThemeIcon variant="transparent" c="white">
                    <IconEdit />
                  </ThemeIcon>
                }
                variant="transparent"
                c="white"
                aria-label={t("car.edit_gallery")}
              >
                {t("car.edit_gallery")}
              </Button>
            ) : null
          }
        />
      </Skeleton>
    </Stack>
  );
};
